// components/Sender/TemplatePreview.js
import React, { useEffect, useMemo, useState } from "react";
import { SenderApi } from "./SenderApi";
import {
  buildWorkerUsageIndex, findOtherUsages,
} from "./workers";

const MODE_LABEL = {
  EMAIL_ONLY: "📧 Email",
  CONTACT_FORM_ONLY: "📝 Форма",
  CALENDLY_ONLY: "📅 Calendly",
  BOTH: "✉️ Email+Форма",
};
const CH_EMAIL = "EMAIL";

const _CHANNEL_ALIASES = {
  "EMAIL": "EMAIL", "E-MAIL": "EMAIL", "E_MAIL": "EMAIL",
  "MAIL": "EMAIL", "EMAIL_ONLY": "EMAIL",
  "CONTACT": "CONTACT_FORM", "CF": "CONTACT_FORM",
  "CONTACT_FORM": "CONTACT_FORM", "CONTACT_FORM_ONLY": "CONTACT_FORM",
  "CALENDLY": "CALENDLY", "CALENDLY_ONLY": "CALENDLY",
  "EMAIL_AND_CONTACT": "EMAIL_AND_CONTACT",
  "CONTACT_AND_EMAIL": "EMAIL_AND_CONTACT",
  "EMAILANDCONTACT": "EMAIL_AND_CONTACT",
  "CONTACTANDEMAIL": "EMAIL_AND_CONTACT",
  "BOTH": "EMAIL_AND_CONTACT",
};
function _normChannel(raw) {
  const s = String(raw || "").trim().toUpperCase();
  if (!s) return "EMAIL";
  return _CHANNEL_ALIASES[s] || s;
}
function _runtimeKeyFor(rawChannel) {
  const ch = _normChannel(rawChannel);
  if (ch === "CONTACT_FORM") return "CONTACT_FORM";
  if (ch === "CALENDLY") return "CALENDLY";
  return "EMAIL";
}
function _channelIcon(ch) {
  if (ch === "EMAIL") return "📧";
  if (ch === "CONTACT_FORM") return "📝";
  if (ch === "CALENDLY") return "📅";
  if (ch === "EMAIL_AND_CONTACT") return "✉️";
  return "❔";
}

// ═══════════════════════════════════════════════════════════════
// Кэш объёмов сегментов (30 сек TTL)
// ═══════════════════════════════════════════════════════════════
const _SEG_COUNTS_TTL_MS = 30_000;
const _segCountsCache = new Map();

async function _loadSegmentCounts(segmentIds, segmentsById) {
  const now = Date.now();
  const result = {};
  const toFetch = [];

  for (const id of segmentIds) {
    const cached = _segCountsCache.get(id);
    if (cached && (now - cached.ts) < _SEG_COUNTS_TTL_MS) {
      result[id] = cached;
      continue;
    }
    if (!segmentsById[id]) {
      result[id] = { ts: now, error: "сегмент не найден" };
      continue;
    }
    toFetch.push(id);
  }

  const fetched = await Promise.all(
    toFetch.map(async (id) => {
      try {
        const seg = segmentsById[id];
        const data = await SenderApi.filterOptions(seg.filters || {});
        const c = (data && data.counts) || {};
        const entry = {
          ts: Date.now(),
          data: {
            total: c.total ?? 0,
            raw_total: c.raw_total ?? c.total ?? 0,
            doubled: c.doubled ?? 0,
          },
        };
        _segCountsCache.set(id, entry);
        return [id, entry];
      } catch (e) {
        const entry = { ts: Date.now(), error: e.message || "ошибка" };
        _segCountsCache.set(id, entry);
        return [id, entry];
      }
    })
  );

  for (const [id, entry] of fetched) result[id] = entry;
  return result;
}

function useSegmentCounts(template, segments) {
  const segmentsById = useMemo(() => {
    const m = {};
    (segments || []).forEach((s) => { m[s.id] = s; });
    return m;
  }, [segments]);

  const uniqueIds = useMemo(() => {
    const s = new Set();
    (template.segment_blocks || []).forEach((b) => {
      if (b.segment_id) s.add(b.segment_id);
    });
    return Array.from(s);
  }, [template]);

  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const depsKey = uniqueIds.slice().sort().join(",");

  useEffect(() => {
    if (!uniqueIds.length) { setCounts({}); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const r = await _loadSegmentCounts(uniqueIds, segmentsById);
      if (!cancelled) {
        setCounts(r);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey]);

  return { counts, loading };
}

// ═══════════════════════════════════════════════════════════════
// Сводка «Воркеры на повторении» сверху превью
// ═══════════════════════════════════════════════════════════════
function _renderDuplicatesBlock(usageIndex) {
  const duplicates = [];

  Object.entries(usageIndex.byGroup).forEach(([groupKey, entries]) => {
    const blocksSet = new Set(entries.map((e) => e.blockIdx));
    if (blocksSet.size < 2) return;

    const byBlock = {};
    entries.forEach((e) => {
      if (!byBlock[e.blockIdx]) {
        byBlock[e.blockIdx] = {
          blockIdx: e.blockIdx,
          blockNum: e.blockNum,
          segmentName: e.segmentName,
          channels: [],
          percents: [],
        };
      }
      byBlock[e.blockIdx].channels.push(e.channel);
      byBlock[e.blockIdx].percents.push(e.percent);
    });

    const blocks = Object.values(byBlock).sort((a, b) => a.blockNum - b.blockNum);

    // Любое из повторяющихся имён. Tooltip — все имена группы, если разные.
    const uniqueNames = [...new Set(entries.map((e) => e.workerName))];
    const displayName = uniqueNames[0];
    const tooltipText = uniqueNames.length > 1
      ? `Группа (${groupKey}):\n${uniqueNames.join("\n")}`
      : uniqueNames[0];

    duplicates.push({
      groupKey,
      displayName,
      tooltipText,
      blocks,
    });
  });

  if (!duplicates.length) return null;

  // Сначала самые «популярные» группы (больше блоков).
  duplicates.sort((a, b) => b.blocks.length - a.blocks.length);

  return (
    <div style={{
      marginBottom: 12, padding: 12,
      background: "#fffbeb", border: "1px solid #fde68a",
      borderRadius: 10,
    }}>
      <div style={{
        fontWeight: 700, fontSize: 13, color: "#92400e",
        marginBottom: 8, display: "flex", alignItems: "center", gap: 6,
      }}>
        ⚠️ Воркеры на повторении ({duplicates.length})
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {duplicates.map((d) => (
          <div key={d.groupKey} style={{
            fontSize: 12, color: "#78350f",
            fontFamily: "Consolas, monospace",
            display: "flex", flexWrap: "wrap", alignItems: "baseline",
            gap: 4,
          }}>
            <b style={{ color: "#92400e", minWidth: 60 }}
               title={d.tooltipText}>
              {d.displayName}
            </b>
            <span style={{ color: "#a16207" }}>→</span>
            {d.blocks.map((b, i) => {
              const chs = [...new Set(b.channels)]
                .map((c) => _channelIcon(_normChannel(c)))
                .join("+");
              return (
                <span key={b.blockIdx}>
                  {i > 0 && <span style={{ color: "#a16207" }}>, </span>}
                  <b>#{b.blockNum}</b>{" "}
                  <span style={{ color: "#6b7280" }}>
                    ({b.segmentName}
                    {chs && <> · {chs}</>}
                    {" · "}
                    {[...new Set(b.percents.map((p) => `${p}%`))].join("/")})
                  </span>
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Строки воркеров и объёма сегмента
// ═══════════════════════════════════════════════════════════════
function _renderAlsoUsedLine(inOthers) {
  if (!inOthers.length) return null;

  const grouped = {};
  for (const u of inOthers) {
    if (!grouped[u.blockNum]) {
      grouped[u.blockNum] = {
        blockNum: u.blockNum,
        segmentName: u.segmentName,
        channels: [],
      };
    }
    grouped[u.blockNum].channels.push(u.channel);
  }
  const items = Object.values(grouped).sort((a, b) => a.blockNum - b.blockNum);
  const shown = items.slice(0, 3);
  const rest = items.length - shown.length;

  return (
    <div style={{
      marginTop: 1, marginLeft: 20,
      fontSize: 11, color: "#b45309",
    }}>
      ↳ 📊 также в{" "}
      {shown.map((u, i) => {
        const chs = [...new Set(u.channels)]
          .map((c) => _channelIcon(_normChannel(c)))
          .join("+");
        return (
          <span key={u.blockNum}>
            {i > 0 && ", "}
            #{u.blockNum} ({u.segmentName}
            {chs && <> · {chs}</>})
          </span>
        );
      })}
      {rest > 0 && <span> … и ещё {rest}</span>}
    </div>
  );
}

function _renderWorkerLine(w, blockDefaults, keyPrefix, inOthers) {
  const rtKey = _runtimeKeyFor(w.channel);
  const inherit = blockDefaults?.[rtKey] || null;
  const explicit = w.runtime_id || null;

  let rtBadge;
  if (explicit) {
    rtBadge = (
      <span style={{
        color: "#0891b2", fontSize: 11,
        fontFamily: "Consolas, monospace", marginLeft: 6,
      }}>🖥 {explicit}</span>
    );
  } else if (inherit) {
    rtBadge = (
      <span style={{
        color: "#6b7280", fontSize: 11,
        fontFamily: "Consolas, monospace", marginLeft: 6,
      }}>↳ {inherit}</span>
    );
  } else {
    rtBadge = (
      <span style={{ color: "#9ca3af", fontSize: 11, marginLeft: 6 }}>
        ↳ любой
      </span>
    );
  }

  const ch = _normChannel(w.channel);
  const chIcon = ch === "EMAIL" ? "📧" : _channelIcon(ch);

  return (
    <div key={keyPrefix}>
      <div style={{ fontFamily: "monospace", fontSize: 12 }}>
        {chIcon} {w.worker_name} — {w.percent}%
        {w.limit ? ` lim=${w.limit}` : ""}
        {rtBadge}
      </div>
      {_renderAlsoUsedLine(inOthers)}
    </div>
  );
}

function _renderSegmentSize(entry, blockLimit, limitMode, loading) {
  if (loading && !entry) {
    return (
      <span style={{ color: "#9ca3af", fontSize: 11, marginLeft: 6 }}>
        · считаем…
      </span>
    );
  }
  if (!entry) return null;
  if (entry.error) {
    return (
      <span style={{ color: "#b45309", fontSize: 11, marginLeft: 6 }}>
        · ⚠️ {entry.error}
      </span>
    );
  }

  const raw = entry.data.raw_total;
  const total = entry.data.total;
  const doubled = entry.data.doubled;

  if (limitMode === "segment_percent" && blockLimit > 0) {
    const eff = Math.round(raw * blockLimit / 100);
    return (
      <span style={{ color: "#0891b2", fontSize: 11, marginLeft: 6 }}>
        · в сегменте {raw} → лимит {eff}
      </span>
    );
  }

  return (
    <span style={{ color: "#0891b2", fontSize: 11, marginLeft: 6 }}>
      · в сегменте: {raw}
      {doubled > 0 && (
        <span style={{ color: "#6b7280" }}> (+{doubled} сдвоенных)</span>
      )}
      {total !== raw && (
        <span style={{ color: "#9ca3af" }}> / таргетов {total}</span>
      )}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// TemplatePreview
// ═══════════════════════════════════════════════════════════════
export function TemplatePreview({ template, segments }) {
  const segById = useMemo(() => {
    const m = {};
    (segments || []).forEach((s) => { m[s.id] = s; });
    return m;
  }, [segments]);

  const { counts, loading } = useSegmentCounts(template, segments);

  const usageIndex = useMemo(
    () => buildWorkerUsageIndex(template.segment_blocks || [], segById),
    [template, segById]
  );

  const blocks = template.segment_blocks || [];

  return (
    <div>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
        Общий лимит: <b>{template.total_limit || "∞"}</b> ·{" "}
        Пауза: <b>{(template.pause_min || 0).toFixed?.(1) ?? 0}–
        {(template.pause_max || 0).toFixed?.(1) ?? 0} сек</b>
        {template.test_run && <> · 🧪 TEST</>}
      </div>

      {/* ═══ Сводка повторов СВЕРХУ ═══ */}
      {_renderDuplicatesBlock(usageIndex)}

      {blocks.length === 0 ? (
        <p style={{ color: "#9ca3af", fontSize: 13 }}>Блоков нет.</p>
      ) : (
        blocks.map((b, i) => {
          const seg = segById[b.segment_id];
          const emailWs = (b.workers || []).filter(
            (w) => (w.channel || CH_EMAIL) === CH_EMAIL);
          const cfWs = (b.workers || []).filter(
            (w) => (w.channel || CH_EMAIL) !== CH_EMAIL);
          const rtDefaults = b.runtime_defaults || {};
          const defEntries = Object.entries(rtDefaults)
            .filter(([, v]) => v);
          const segCountEntry = counts[b.segment_id];

          return (
            <div key={i} style={{
              marginBottom: 10, padding: 10, background: "#f9fafb",
              borderRadius: 8, border: "1px solid #e5e7eb",
            }}>
              <div style={{
                fontWeight: 700, color: "#3730a3", fontSize: 13,
                display: "flex", alignItems: "center",
                flexWrap: "wrap", gap: 8,
              }}>
                <span>
                  #{i + 1} · {seg?.name || `⚠️ ${b.segment_id}`} ·{" "}
                  {MODE_LABEL[b.mode] || b.mode} ·{" "}
                  {b.fetch_mode === "random" ? "🎲" : "📋"} ·{" "}
                  лимит: {b.limit_mode === "segment_percent"
                    ? `${b.limit}% сегм.` : (b.limit || "∞")}
                  {_renderSegmentSize(
                    segCountEntry, b.limit, b.limit_mode, loading)}
                </span>
                {defEntries.length > 0 && (
                  <span style={{
                    fontSize: 11, fontWeight: 400, color: "#0891b2",
                    background: "#e0f2fe", padding: "2px 8px",
                    borderRadius: 6,
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}>
                    {defEntries.map(([ch, rt]) => (
                      <span key={ch} style={{
                        fontFamily: "Consolas, monospace",
                      }}>
                        {_channelIcon(_normChannel(ch))} {rt}
                      </span>
                    ))}
                  </span>
                )}
              </div>
              <div style={{ marginTop: 6, fontSize: 12 }}>
                {emailWs.map((w, j) => _renderWorkerLine(
                  w, rtDefaults, `e${j}`,
                  findOtherUsages(usageIndex, w.worker_name, i)
                ))}
                {cfWs.map((w, j) => _renderWorkerLine(
                  w, rtDefaults, `c${j}`,
                  findOtherUsages(usageIndex, w.worker_name, i)
                ))}
                {!emailWs.length && !cfWs.length && (
                  <span style={{ color: "#9ca3af" }}>— воркеров нет —</span>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}